import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { playersApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Users, Swords, Globe } from 'lucide-react';

const COUNTRY_FLAGS = {
  'ZA': '\ud83c\uddff\ud83c\udde6', 'US': '\ud83c\uddfa\ud83c\uddf8', 'GB': '\ud83c\uddec\ud83c\udde7', 'NG': '\ud83c\uddf3\ud83c\uddec',
  'KE': '\ud83c\uddf0\ud83c\uddea', 'IN': '\ud83c\uddee\ud83c\uddf3', 'BR': '\ud83c\udde7\ud83c\uddf7', 'DE': '\ud83c\udde9\ud83c\uddea',
  'FR': '\ud83c\uddeb\ud83c\uddf7', 'AU': '\ud83c\udde6\ud83c\uddfa', 'CA': '\ud83c\udde8\ud83c\udde6', 'GH': '\ud83c\uddec\ud83c\udded',
};

const SubscriberBadge = ({ tier }) => {
  if (tier === 'gold' || tier === 'premium') return <span className="inline-flex items-center gap-0.5 bg-[#D4AF37] text-[#1A1A1A] text-xs px-1.5 py-0.5 rounded-full font-bold">GOLD</span>;
  if (tier === 'green' || tier === 'pro') return <span className="inline-flex items-center gap-0.5 bg-[#28A745] text-white text-xs px-1.5 py-0.5 rounded-full font-bold">GREEN</span>;
  return null;
};

const OnlinePlayersPanel = () => {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await playersApi.getOnline();
        setPlayers(data || []);
      } catch (e) {} finally { setLoading(false); }
    };
    fetch();
    const interval = setInterval(fetch, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="card-dark" data-testid="online-players-panel">
      <h3 className="font-bold text-white flex items-center gap-2 mb-3">
        <Users className="h-5 w-5 text-[#28A745]" />
        Online Players
        <span className="bg-[#28A745] text-white text-xs px-2 py-0.5 rounded-full font-bold ml-auto">{players.length}</span>
      </h3>
      {loading ? (
        <div className="text-center py-4 text-gray-500 text-sm">Loading...</div>
      ) : players.length === 0 ? (
        <div className="text-center py-4 text-gray-500 text-sm">No players online</div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {players.map((p) => (
            <div key={p.id} className="flex items-center gap-2 p-2 bg-[#2D2D2D] rounded-lg" data-testid={`online-player-${p.id}`}>
              <div className="relative flex-shrink-0">
                <div className="w-9 h-9 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#1A1A1A] font-bold text-sm">
                  {p.full_name?.charAt(0)}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#28A745] border-2 border-[#2D2D2D]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="text-white text-sm font-semibold truncate">{p.full_name}</span>
                  <SubscriberBadge tier={p.subscription_tier} />
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-1">
                  <span>{COUNTRY_FLAGS[p.country] || ''}</span>
                  <Globe className="h-3 w-3" />
                  <span>{p.country || 'Unknown'}</span>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/rps')} 
                size="sm" 
                className="bg-[#D4AF37] text-[#1A1A1A] text-xs hover:bg-[#C4A037] px-2 py-1 h-auto"
                data-testid={`challenge-player-${p.id}`}
              >
                <Swords className="h-3 w-3 mr-1" /> Challenge
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export { OnlinePlayersPanel, SubscriberBadge };
export default OnlinePlayersPanel;
